import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Reveal from '@/components/common/Reveal';
import BrowserFrame from '@/components/common/BrowserFrame';
import { projects } from '@/data/projects';
import { buttonVariants } from '@/components/ui/button';

const featured = projects.filter(p => p.featured && !p.placeholder);

const countLabel = n => ['One', 'Two', 'Three', 'Four', 'Five'][n - 1] || `${n}`;

export const FeaturedWork = () => (
  <section id="work" className="py-24 md:py-32 bg-secondary/50 border-y border-border ">
    <div className="mx-auto container-editorial">
      <div className="flex items-end justify-between mb-12 md:mb-16">
        <Reveal>
          <div className="label-eyebrow mb-4">Chapter — Featured Work</div>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-balance max-w-3xl">
            {countLabel(featured.length)} sites <span className="italic-serif text-sienna">worth a closer look.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="hidden md:block">
          <Link to="/work" className={buttonVariants({ variant: 'ghost-editorial' })}>
            All work <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-10">
        {/* Large left — first featured */}
        {featured[0] && (
          <Reveal className="md:col-span-2 lg:col-span-7">
            <FeaturedCard project={featured[0]} large />
          </Reveal>
        )}
        {/* Two right — remaining featured */}
        {featured.slice(1).length > 0 && (
          <div className="md:col-span-2 lg:col-span-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-8 md:gap-10">
            {featured.slice(1, 3).map((p, i) => (
              <Reveal key={p.slug} delay={(i + 1) * 0.1}>
                <FeaturedCard project={p} />
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {/* Button below all cards */}
      <Reveal className="mt-12 flex justify-center ">
        <Link
            to="/work"
            className={buttonVariants({ variant: 'editorial' })}
        >
          See All work
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>

  </section>

);

const FeaturedCard = ({ project, large = false }) => (
  <a
    href={project.url || '#'}
    target={project.url ? '_blank' : undefined}
    rel="noreferrer"
    className="group block"
  >
    <BrowserFrame
      src={project.image}
      alt={project.name}
      aspect={large ? 'aspect-[16/10] md:aspect-[16/11]' : 'aspect-[16/10] md:aspect-[16/11]'}
      url={project.url}
    />

    <div className="mt-5 flex items-start justify-between gap-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3 mb-1.5">
          <span className="label-eyebrow">{project.industry}</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <span className="label-eyebrow">{project.location}</span>
          {project.concept && (
            <>
              <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
              <span className="label-eyebrow px-2 py-0.5 rounded-sm bg-secondary text-sienna border border-border/50">Concept</span>
            </>
          )}
        </div>
        <h3 className={`font-serif tracking-tight leading-tight group-hover:text-sienna transition-colors ${large ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl'}`}>
          {project.name}
        </h3>
        {large && (
          <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-md leading-relaxed">
            {project.description}
          </p>
        )}
      </div>
      <span className="mt-1 shrink-0 w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:bg-foreground group-hover:text-background group-hover:border-foreground transition-colors">
        <ArrowUpRight className="w-4 h-4" />
      </span>
    </div>
  </a>
);

export default FeaturedWork;
